import type { Place } from './usePlaces';
import type { AiMode } from './useSettings';
import { inferOpenStatus } from '@/utils/openingHoursInference';
import { buildLowPrompt as buildLowPromptNew, buildMediumPrompt as buildMediumPromptNew } from '@/prompts/ranking';
import { callLLM, parseJsonArray, llmFingerprint } from '@/services/llmClient';
import { buildRealTravelTimes, estimateDistanceNote, formatDistanceNote, travelStrings } from '@/utils/travelTime';
import { reportError } from '@/lib/reportError';

export type RankingSource = 'ai' | 'offline';

export interface RankedPlace extends Place {
  hook: string;
  estimatedDuration: string;
  category: string;
  distanceNote: string;
  matchingVibes: string[];
  practicalityRank?: number;
  realTravelMins?: number;
  realTravelText?: string;
  travelTimeIsEstimated?: boolean;
  /** Who ranked and described this place: the AI, or the offline scorer
   *  because the AI was unavailable. Drives the results banner and what a
   *  saved trip records. */
  source: RankingSource;
  /** Model id when source is 'ai'. */
  model?: string;
}

const VIBE_TYPE_MAP: Record<string, string[]> = {
  hungry: ['restaurant', 'cafe', 'bakery', 'bar', 'meal_takeaway', 'food', 'coffee_shop'],
  'must-sees': ['tourist_attraction', 'museum', 'art_gallery', 'church', 'park', 'landmark'],
  adventurous: ['park', 'national_park', 'hiking_area', 'amusement_park', 'zoo', 'aquarium', 'sports_complex'],
  'hidden-gems': ['bar', 'cafe', 'book_store', 'art_gallery', 'market', 'night_club'],
  popular: ['tourist_attraction', 'restaurant', 'shopping_mall', 'museum', 'park'],
  'feeling-lucky': ['night_club', 'casino', 'spa', 'movie_theater', 'bowling_alley', 'amusement_park', 'bar'],
};

function matchVibesForPlace(placeTypes: string[], vibes: string[]): string[] {
  const matched: string[] = [];
  for (const vibe of vibes) {
    const vibeTypes = VIBE_TYPE_MAP[vibe] || [];
    if (placeTypes.some(t => vibeTypes.some(vt => t.includes(vt)))) {
      matched.push(vibe);
    }
  }
  return matched.length > 0 ? matched : [vibes[0]];
}

function estimateDuration(types: string[]): string {
  const foodTypes = ['restaurant', 'cafe', 'bakery', 'bar', 'coffee_shop'];
  const quickTypes = ['store', 'shop', 'market', 'book_store'];
  const longTypes = ['museum', 'park', 'national_park', 'amusement_park', 'zoo', 'aquarium'];

  if (types.some(t => longTypes.some(lt => t.includes(lt)))) return '1-2 hours';
  if (types.some(t => foodTypes.some(ft => t.includes(ft)))) return '30-60 mins';
  if (types.some(t => quickTypes.some(qt => t.includes(qt)))) return '15-30 mins';
  return '30-60 mins';
}

export function useLLM() {
  // Offline cards carry facts only: Google's own editorial summary when it
  // exists, otherwise nothing. Rating, reviews, travel time and open status
  // are already on the card (#87 §6).
  const rankPlacesOff = (
    places: Place[],
    transport: string,
    vibes: string[],
    timeAvailable: string,
    language: string
  ): RankedPlace[] => {
    const isShortWindow = timeAvailable === '1-2-hrs' || timeAvailable === 'half-day';
    const currentHour = new Date().getHours();

    const realTravelTimes = buildRealTravelTimes(places, language);
    const localSpot = travelStrings(language).localSpot;

    const openPlaces = isShortWindow
      ? places.filter(p => {
          const travelMins = p.routingDurationSeconds ? Math.round(p.routingDurationSeconds / 60) : 0;
          const status = inferOpenStatus(p.currentOpeningHours, p.types, currentHour, travelMins);
          return status.likelyOpen;
        })
      : places;
    const pool = openPlaces.length > 0 ? openPlaces : places;

    const scored = pool.map(p => {
      const ratingScore = (p.rating || 3) * 2;
      const reviewScore = Math.min((p.userRatingCount || 0) / 500, 2);
      const distPenalty = p.distanceMeters ? Math.min(p.distanceMeters / 5000, 3) : 0;
      const travelMins = p.routingDurationSeconds ? Math.round(p.routingDurationSeconds / 60) : 0;
      const status = inferOpenStatus(p.currentOpeningHours, p.types, currentHour, travelMins);
      const openScore = status.likelyOpen
        ? (status.confidence === 'known' ? 3 : 1.5)
        : (status.confidence === 'known' ? -2 : -1);
      const score = ratingScore + reviewScore - distPenalty + openScore;

      const realTime = realTravelTimes.get(p.id);
      return {
        place: p,
        score,
        ranked: {
          ...p,
          hook: p.editorialSummary ?? '',
          estimatedDuration: estimateDuration(p.types),
          category: p.types[0]?.replace(/_/g, ' ') || localSpot,
          distanceNote: realTime ? formatDistanceNote(realTime.mins, transport, language) : estimateDistanceNote(p, transport, language),
          matchingVibes: matchVibesForPlace(p.types, vibes),
          practicalityRank: Math.max(1, Math.min(10, Math.round(score))),
          realTravelMins: realTime?.mins,
          realTravelText: realTime?.text,
          travelTimeIsEstimated: !realTime,
          source: 'offline',
        } as RankedPlace,
      };
    });

    scored.sort((a, b) => b.score - a.score);
    return scored.map(s => s.ranked);
  };

  // Pre-rank and cap candidates before sending to LLM.
  // Reduces token count for medium/high without sacrificing quality:
  // quality = rating × log(reviews) penalised by distance.
  const trimPlacesForMode = (places: Place[], mode: AiMode): Place[] => {
    if (mode === 'low') return trimPlacesForLowMode(places);

    const cap = mode === 'high' ? 40 : 25;
    if (places.length <= cap) return places;

    const scored = places.map(p => ({
      place: p,
      score: (p.rating ?? 0) * Math.log10(Math.max(p.userRatingCount ?? 1, 1))
        - (p.distanceMeters ?? 10000) / 5000,
    }));
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, cap).map(s => s.place);
  };

  const trimPlacesForLowMode = (places: Place[]): Place[] => {
    const CAP = 12;
    if (places.length <= CAP * 2) return places;

    const byDistance = [...places]
      .filter(p => p.distanceMeters != null)
      .sort((a, b) => (a.distanceMeters ?? Infinity) - (b.distanceMeters ?? Infinity))
      .slice(0, CAP);

    const byRating = [...places]
      .sort((a, b) => {
        const scoreA = (a.rating ?? 0) * Math.log10(Math.max(a.userRatingCount ?? 1, 1));
        const scoreB = (b.rating ?? 0) * Math.log10(Math.max(b.userRatingCount ?? 1, 1));
        return scoreB - scoreA;
      })
      .slice(0, CAP);

    const seen = new Set<string>();
    const merged: Place[] = [];
    for (const p of [...byDistance, ...byRating]) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        merged.push(p);
      }
    }
    return merged;
  };

  // buildLowPrompt and buildMediumPrompt (dead code) removed as part
  // of H-103b typecheck cleanup. The callers below use buildLowPromptNew
  // and buildMediumPromptNew imported from @/prompts/ranking.
  const parseLLMRankedResponse = (
    responseText: string,
    places: Place[],
    vibes: string[],
    transport: string,
    realTravelTimes: Map<string, { mins: number; text: string }>,
    isLowMode: boolean,
    model: string,
    language: string
  ): RankedPlace[] => {
    const rankedData = parseJsonArray(responseText);
    if (rankedData.length === 0) return [];
    const localSpot = travelStrings(language).localSpot;

    const rankedPlaces: RankedPlace[] = [];
    const usedIndices = new Set<number>();

    interface LLMRankedItem {
      idx: number | string;
      hook?: string;
      matchingVibes?: string[] | string;
      estimatedDuration?: string;
      category?: string;
      distanceNote?: string;
      practicalityRank?: number;
    }

    rankedData.forEach((rankedRaw: unknown) => {
      const ranked = (rankedRaw ?? {}) as LLMRankedItem;
      const idx = typeof ranked.idx === 'number' ? ranked.idx : parseInt(String(ranked.idx), 10);
      if (isNaN(idx) || idx < 0 || idx >= places.length) return;
      if (usedIndices.has(idx)) return;

      const originalPlace = places[idx];
      usedIndices.add(idx);

      const rawMatchingVibes = Array.isArray(ranked.matchingVibes)
        ? ranked.matchingVibes.filter((v) => typeof v === 'string' ? v.trim() !== '' : !!v)
        : typeof ranked.matchingVibes === 'string'
          ? (ranked.matchingVibes.trim() ? [ranked.matchingVibes.trim()] : [])
          : matchVibesForPlace(originalPlace.types, vibes);
      // Scuba bug fix: the prompt describes custom vibes as bare labels (e.g. "scuba")
      // but asks the LLM to return them from a list that contains the prefixed form
      // (e.g. "custom:scuba"). LLMs pick either. Re-map any returned value back to
      // the canonical input-vibe shape the UI filters against.
      const normalizedVibes = (rawMatchingVibes as string[]).map((v: string) => {
        if (typeof v !== 'string') return v;
        if (vibes.includes(v)) return v;
        const prefixed = `custom:${v}`;
        if (vibes.includes(prefixed)) return prefixed;
        const stripped = v.startsWith('custom:') ? v.slice(7) : v;
        if (vibes.includes(stripped)) return stripped;
        // Case-insensitive match against custom label
        const lower = v.toLowerCase();
        const match = vibes.find(iv =>
          iv.toLowerCase() === lower ||
          iv.toLowerCase() === `custom:${lower}` ||
          (iv.startsWith('custom:') && iv.slice(7).toLowerCase() === lower)
        );
        return match ?? v;
      });

      const realTime = realTravelTimes.get(originalPlace.id);

      if (isLowMode) {
        const distNote = realTime
          ? formatDistanceNote(realTime.mins, transport, language)
          : estimateDistanceNote(originalPlace, transport, language);

        rankedPlaces.push({
          ...originalPlace,
          hook: ranked.hook || originalPlace.editorialSummary || '',
          estimatedDuration: estimateDuration(originalPlace.types),
          category: originalPlace.types[0]?.replace(/_/g, ' ') || localSpot,
          distanceNote: distNote,
          matchingVibes: normalizedVibes,
          realTravelMins: realTime?.mins,
          realTravelText: realTime?.text,
          travelTimeIsEstimated: !realTime,
          source: 'ai',
          model,
        });
      } else {
        // Fill missing fields from offline fallbacks instead of silently dropping
        const hook = ranked.hook || originalPlace.editorialSummary || '';
        const estDuration = ranked.estimatedDuration || estimateDuration(originalPlace.types);
        const category = ranked.category || originalPlace.types[0]?.replace(/_/g, ' ') || localSpot;

        if (!ranked.hook) {
          console.warn(`[LLM] Place "${originalPlace.name}" (idx ${idx}) missing hook — using fallback`);
        }

        rankedPlaces.push({
          ...originalPlace,
          hook,
          estimatedDuration: estDuration,
          category,
          distanceNote: ranked.distanceNote || (realTime ? formatDistanceNote(realTime.mins, transport, language) : estimateDistanceNote(originalPlace, transport, language)),
          matchingVibes: normalizedVibes,
          practicalityRank: typeof ranked.practicalityRank === 'number' ? ranked.practicalityRank : undefined,
          realTravelMins: realTime?.mins,
          realTravelText: realTime?.text,
          travelTimeIsEstimated: !realTime,
          source: 'ai',
          model,
        });
      }
    });

    return rankedPlaces;
  };

  const rankPlaces = async (
    places: Place[],
    location: string,
    timeAvailable: string,
    transport: string,
    vibes: string[],
    language: string = 'en',
    aiMode: AiMode = 'low',
    tasteHint?: string | null,
    weatherLine?: string | null
  ): Promise<RankedPlace[]> => {
    if (aiMode === 'off') return rankPlacesOff(places, transport, vibes, timeAvailable, language);

    // The AI failed or returned nothing usable: the scored offline ranker
    // substitutes, the results are labelled, and Sentry hears about it once
    // per failing provider and model (#87 §4).
    const substitute = (candidates: Place[], err: unknown, mode: AiMode): RankedPlace[] => {
      console.warn(`LLM ${mode}-mode failed, using offline ranking:`, err);
      reportError('llm.ranking', err, {
        mode, candidates: candidates.length, timeAvailable, transport, language,
        attempts: err instanceof Error && 'attempts' in err ? (err as { attempts: unknown }).attempts : undefined,
      }, { level: 'warning', fingerprint: llmFingerprint('llm.ranking', err) });
      return rankPlacesOff(candidates, transport, vibes, timeAvailable, language);
    };

    const isLow = aiMode === 'low';
    const trimmed = isLow ? trimPlacesForLowMode(places) : trimPlacesForMode(places, aiMode);
    if (trimmed.length < places.length) {
      console.log(`${aiMode} mode: trimmed ${places.length} -> ${trimmed.length} candidates before LLM`);
    }
    const realTravelTimes = buildRealTravelTimes(trimmed, language);

    try {
      const { system, user } = isLow
        ? buildLowPromptNew(trimmed, location, vibes, language, transport, timeAvailable, tasteHint, weatherLine)
        : buildMediumPromptNew(trimmed, location, timeAvailable, transport, vibes, language, realTravelTimes, tasteHint, weatherLine);
      const maxTokens = isLow ? 9000 : aiMode === 'high' ? 12000 : 10000;
      const result = await callLLM({ prompt: user, systemPrompt: system, maxTokens, schema: isLow ? 'ranking-low' : 'ranking-medium' });
      const ranked = parseLLMRankedResponse(result.text, trimmed, vibes, transport, realTravelTimes, isLow, result.model, language);
      console.log(`[LLM] ${aiMode} mode via ${result.provider}/${result.model}: ${trimmed.length} sent → ${ranked.length} returned`);
      if (ranked.length > 0) return ranked;
      return substitute(trimmed, new Error(`${result.model} returned no usable places`), aiMode);
    } catch (llmErr) {
      return substitute(trimmed, llmErr, aiMode);
    }
  };

  return { rankPlaces };
}
